import React, { useRef, useState, useEffect } from 'react';
import { Camera, X, Images, Check, AlertCircle, Loader2 } from 'lucide-react';
import { storeTemporaryPhoto } from '../services/photoService';
import { enqueuePhotoUploads } from '../services/uploadQueue';
import { validateImageFile } from '../utils/imageFormatConverter';

interface CameraComponentProps {
  projectId: string;
  roomId?: string;
  itemId?: string;
  /**
   * Fires once per photo, after it has been persisted (uploaded, or stored as a
   * temporary photo). The camera stays open so the user can keep shooting —
   * parents should refresh their photo list here, not close the camera.
   * Uploads run in the app-wide queue, so this may fire long after the camera
   * has been closed or the user has left the page.
   */
  onPhotoTaken: (photoId: string) => void;
  /** The user closed the camera. Uploads still in flight keep running in the background. */
  onClose: () => void;
}

type ShotStatus = 'saving' | 'saved' | 'failed';

interface Shot {
  key: number;
  preview: string;
  status: ShotStatus;
}

/**
 * Photo capture screen. Shooting hands off to the phone's native camera app via
 * a `capture` file input, so every shot gets the full-resolution, stabilised,
 * HDR-processed still the Camera app produces. A getUserMedia viewfinder can
 * only grab ~2 MP video frames on iOS (Safari has no ImageCapture API), which
 * is why the previous in-app shutter produced visibly blurrier photos.
 */
const CameraComponent: React.FC<CameraComponentProps> = ({
  projectId,
  roomId,
  itemId,
  onPhotoTaken,
  onClose,
}) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);

  // Upload callbacks outlive renders (and possibly the component itself), so
  // they read the latest prop through a ref instead of a stale closure.
  const onPhotoTakenRef = useRef(onPhotoTaken);
  const shotCounter = useRef(0);
  const previewUrls = useRef<string[]>([]);
  useEffect(() => { onPhotoTakenRef.current = onPhotoTaken; }, [onPhotoTaken]);
  // Previews are object URLs; release them when the screen goes away.
  useEffect(() => () => { previewUrls.current.forEach(url => URL.revokeObjectURL(url)); }, []);

  const updateShot = (key: number, status: ShotStatus) => {
    setShots(prev => prev.map(s => (s.key === key ? { ...s, status } : s)));
  };

  const addShot = (blob: Blob): number => {
    const key = ++shotCounter.current;
    const preview = URL.createObjectURL(blob);
    previewUrls.current.push(preview);
    setShots(prev => [...prev, { key, preview, status: 'saving' }]);
    return key;
  };

  // Temporary photos (before a project exists) live in localStorage as data URLs.
  const saveTemporary = (blob: Blob, key: number) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const photoId = storeTemporaryPhoto(reader.result as string);
        updateShot(key, 'saved');
        onPhotoTakenRef.current(photoId);
      } catch (err) {
        console.error('Error storing temporary photo:', err);
        updateShot(key, 'failed');
        setError('Kunde inte spara fotot lokalt. Försök igen.');
      }
    };
    reader.onerror = () => {
      updateShot(key, 'failed');
      setError('Kunde inte läsa bilden. Försök igen.');
    };
    reader.readAsDataURL(blob);
  };

  // Hand photos to the background upload queue and return at once. The queue
  // keeps uploading after the camera closes and after the user leaves the project.
  const savePhotos = (blobs: Blob[]) => {
    const keys = blobs.map(addShot);

    if (projectId === 'temp') {
      blobs.forEach((blob, i) => saveTemporary(blob, keys[i]));
      return;
    }

    // Job ids come back in the same order as the blobs. Callbacks can't fire
    // before enqueue returns (the first upload awaits the network), so the map
    // is always populated by the time it is read.
    const keyByJob = new Map<string, number>();
    const jobIds = enqueuePhotoUploads(blobs, { projectId, roomId, itemId }, {
      onUploaded: (jobId, photoId) => {
        const key = keyByJob.get(jobId);
        if (key !== undefined) updateShot(key, 'saved');
        onPhotoTakenRef.current(photoId);
      },
      onFailed: (jobId) => {
        const key = keyByJob.get(jobId);
        if (key !== undefined) updateShot(key, 'failed');
      },
    });
    jobIds.forEach((id, i) => keyByJob.set(id, keys[i]));
  };

  // Validate picked files and queue the usable ones. Returns true when every
  // file was accepted, so callers can decide whether to stay or close.
  const queueFiles = (files: File[]): boolean => {
    const valid: Blob[] = [];
    const rejected: string[] = [];
    files.forEach(file => {
      const check = validateImageFile(file);
      if (check.valid) valid.push(file);
      else rejected.push(`${file.name}: ${check.error}`);
    });

    if (rejected.length > 0) {
      setError(
        valid.length === 0
          ? `Ingen bild kunde användas. ${rejected.join(', ')}`
          : `Vissa bilder hoppades över: ${rejected.join(', ')}`
      );
    } else {
      setError(null);
    }
    if (valid.length > 0) savePhotos(valid);
    return rejected.length === 0;
  };

  // Back from the native camera with one shot. Stay on this screen so the next
  // tap on the shutter goes straight back into the camera.
  const handleCameraShot = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // allow the same file again
    if (files.length === 0) return; // user cancelled in the camera
    queueFiles(files);
  };

  // Picked from the photo library, several at once. If every file was usable
  // there is nothing left to do here, so the screen closes and the app-wide
  // progress pill takes over.
  const handleLibraryPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // allow picking the same files again
    if (files.length === 0) return;
    if (queueFiles(files)) handleClose();
  };

  const pendingCount = shots.filter(s => s.status === 'saving').length;
  const savedCount = shots.filter(s => s.status === 'saved').length;

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 lg:left-60 bg-black bg-opacity-90 z-50 flex flex-col">
      {/* Native camera: `capture` makes iOS/Android open the camera app directly, one shot per round trip */}
      <input
        type="file"
        ref={cameraInputRef}
        accept="image/*"
        capture="environment"
        onChange={handleCameraShot}
        className="hidden"
      />
      {/* Photo library: multi-select, no capture so the picker opens instead */}
      <input
        type="file"
        ref={libraryInputRef}
        accept="image/*,.heic,.heif,.avif"
        multiple
        onChange={handleLibraryPicked}
        className="hidden"
      />

      {/* Header — pinned top */}
      <div className="flex-shrink-0 flex justify-between items-center p-4 bg-black" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="text-white text-lg font-semibold">Ta foto</h2>
          {shots.length > 0 && (
            <span
              className="text-white/70 text-sm flex items-center gap-1.5"
              role="status"
              aria-live="polite"
            >
              {pendingCount > 0 ? (
                <>
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                  {savedCount > 0 ? `${savedCount} sparade · ${pendingCount} sparas` : `${pendingCount} sparas`}
                </>
              ) : (
                <>
                  <Check size={14} aria-hidden="true" />
                  {savedCount === 1 ? '1 foto sparat' : `${savedCount} foton sparade`}
                </>
              )}
            </span>
          )}
        </div>
        <button
          onClick={handleClose}
          className="text-white p-2 rounded-full hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Stäng kamera"
        >
          <X size={24} />
        </button>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center p-4">
        {error && (
          <div className="bg-red-500 text-white p-4 rounded-md mb-4 w-full max-w-md" role="alert">
            {error}
          </div>
        )}

        {shots.length === 0 ? (
          <div className="w-full max-w-md flex-1 flex flex-col items-center justify-center text-center text-white/60 gap-3 px-4">
            <Camera size={48} aria-hidden="true" className="text-white/40" />
            <p className="text-base text-white/80">Tryck på knappen för att öppna kameran.</p>
            <p className="text-sm">Varje bild sparas direkt när du väljer &ldquo;Använd bild&rdquo;. Fortsätt fota så många du vill.</p>
          </div>
        ) : (
          <ul className="w-full max-w-md grid grid-cols-4 gap-2" aria-label="Tagna foton">
            {shots.map(shot => (
              <li key={shot.key} className="relative aspect-square rounded-md overflow-hidden bg-white/10">
                <img src={shot.preview} alt="" className="w-full h-full object-cover" />
                {shot.status === 'saving' && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center" aria-label="Sparar">
                    <Loader2 size={18} className="text-white animate-spin" />
                  </div>
                )}
                {shot.status === 'saved' && (
                  <div className="absolute bottom-0.5 right-0.5 bg-emerald-600 text-white rounded-full p-0.5" aria-label="Sparat">
                    <Check size={12} />
                  </div>
                )}
                {shot.status === 'failed' && (
                  <div className="absolute inset-0 bg-red-600/70 flex items-center justify-center" aria-label="Misslyckades">
                    <AlertCircle size={18} className="text-white" />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bottom action bar — pinned to bottom */}
      <div className="flex-shrink-0 bg-black px-4 pt-3" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
        <div className="flex flex-col gap-3 items-center w-full max-w-md mx-auto">
          <button
            onClick={() => cameraInputRef.current?.click()}
            aria-label="Ta foto"
            className="bg-white text-primary-600 p-5 rounded-full shadow-lg active:scale-95 active:bg-white/90 transition-transform flex items-center justify-center"
          >
            <Camera size={32} />
          </button>
          <button
            onClick={() => libraryInputRef.current?.click()}
            className="bg-white/15 text-white px-4 py-2 rounded-md flex items-center gap-2 justify-center min-h-[44px]"
          >
            <Images size={18} aria-hidden="true" />
            <span>Ladda upp bilder istället</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraComponent;
